import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Text as BaseText,
  Box,
  FormControl,
  HStack,
  Icon,
  View,
} from '@rbilabs/universal-components';
import { DatadogSessionReplayMask } from 'features/observability/rum/datadog-session-replay-mask';
import { StyleSheet } from 'react-native';

import { A11yWrapper } from 'components/a11y-wrapper';
import { TextInput } from 'components/ucl/text-input';
import useEffectOnce from 'hooks/use-effect-once';
import { useCartContext } from 'state/cart';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { LANGUAGES } from 'state/intl/types';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { getDesignToken, spacing } from 'styles/constants';
import { enableNumericPhoneValidation } from 'utils/environment';
import { getPhoneNumberErrorMessage, isPhoneNumberValid, requiredString } from 'utils/form';
import { formatDistanceToNow } from 'utils/language/format-date';

import { FreeDeliveryProgressContainer } from '../free-delivery-progress';
import NYCRegulatoryLink from '../nyc-regulatory-link';

import { getLocalizedDeliveryAddress } from './common';
import { ITU_COUNTRY_CODES, PHONE_NUMBER_LENGTH } from './constants';
import PhoneAddressInformationToggle from './phone-address-information-toggle';
import SaveAddressSelection from './save-address-selection';
import { DetailsBox, FieldContainer, InstructionsTextarea, TextStyled } from './styled';
import { ICartDetailsProps } from './types';

const DeliveryDetailsForm: React.FC<React.PropsWithChildren<
  Omit<ICartDetailsProps, 'serviceMode'>
>> = ({
  serverOrder,
  setAreDeliveryDetailsValid,
  sectionHeading,
  phoneNumberInputRef,
  deliveryDetailsErrors,
}) => {
  const {
    deliveryAddress,
    deliveryInstructions,
    orderPhoneNumber,
    setDeliveryAddress,
    setDeliveryInstructions,
    setOrderPhoneNumber,
    updateShouldSaveDeliveryAddress,
  } = useOrderContext();

  const { isOrderPriceLoading } = useCartContext();

  const addressLine2Error = useRef<boolean>(false);
  const phoneNumberWasEdited = useRef<boolean>(false);

  const [editingPhoneNumber, setEditingPhoneNumber] = useState(orderPhoneNumber);
  const [phoneNumberError, setPhoneNumberError] = useState('');
  const hasPhoneNumberErrors = !!deliveryDetailsErrors.phoneNumber || !!phoneNumberError;
  const enableFreeDeliveryCheckoutProgress = useFlag(
    LaunchDarklyFlag.ENABLE_FREE_DELIVERY_CHECKOUT_PROGRESS_BAR
  );
  const enableRequireAddress2 = useFlag(
    LaunchDarklyFlag.ENABLE_DELIVERY_DETAILS_FORM_REQUIRE_ADDRESS_2
  );

  const maxDeliveryTime = serverOrder?.delivery?.dropoff.beforeTime || '';
  const ETA = useMemo(
    () => (maxDeliveryTime ? formatDistanceToNow(new Date(maxDeliveryTime), LANGUAGES.en) : ''),
    [maxDeliveryTime]
  );

  const { region } = useLocale();

  const localizedDeliveryAddress = getLocalizedDeliveryAddress(deliveryAddress);
  const handleOrderPhoneNumberChange = useCallback(
    async (input: string) => {
      phoneNumberWasEdited.current = true;
      const newPhoneNumber = input.replace(/[^+\d]/g, '');
      setEditingPhoneNumber(newPhoneNumber);

      const { valid, error } = await isPhoneNumberValid({
        phoneNumber: newPhoneNumber,
        simpleValidation: enableNumericPhoneValidation(),
        country: region,
      });

      if (valid) {
        setPhoneNumberError('');
        const countryCodePhoneNumber =
          newPhoneNumber.length === PHONE_NUMBER_LENGTH
            ? `${ITU_COUNTRY_CODES[region]}${newPhoneNumber}`
            : newPhoneNumber;
        setOrderPhoneNumber(countryCodePhoneNumber);
      } else {
        setPhoneNumberError(getPhoneNumberErrorMessage(error));
      }

      if (setAreDeliveryDetailsValid) {
        setAreDeliveryDetailsValid(valid);
      }
    },
    [region, setAreDeliveryDetailsValid, setOrderPhoneNumber]
  );
  useEffect(() => {
    if (!phoneNumberWasEdited.current && !editingPhoneNumber) {
      setPhoneNumberError('');
      setEditingPhoneNumber(orderPhoneNumber);
    }
  }, [editingPhoneNumber, orderPhoneNumber]);

  useEffect(() => {
    const addressL2 = deliveryAddress?.addressLine2 || '';
    if (enableRequireAddress2 && addressL2.length <= 0) {
      addressLine2Error.current = true;
    } else {
      addressLine2Error.current = false;
    }
  }, [deliveryAddress, enableRequireAddress2]);

  useEffect(() => {
    if (!phoneNumberWasEdited.current && orderPhoneNumber) {
      setEditingPhoneNumber(orderPhoneNumber);
      // The phone number it's already validated from the backend.
      if (setAreDeliveryDetailsValid) {
        setAreDeliveryDetailsValid(true);
      }
    }
  }, [orderPhoneNumber, setAreDeliveryDetailsValid]);

  const handleUnitNumberChange = (value: string) => {
    updateShouldSaveDeliveryAddress(false);
    setDeliveryAddress(previousDeliveryAddress => {
      if (!previousDeliveryAddress || previousDeliveryAddress.addressLine2 === value) {
        return previousDeliveryAddress;
      }
      return {
        ...previousDeliveryAddress,
        addressLine2: value,
      };
    });
    if (enableRequireAddress2) {
      addressLine2Error.current = !requiredString(value);
    }
  };

  // Trigger form validation on the first render to ensure any errors
  // in preloaded or BE user data are immediately displayed in the UI.
  useEffectOnce(() => {
    setEditingPhoneNumber(editingPhoneNumber);

    isPhoneNumberValid({
      phoneNumber: editingPhoneNumber,
      simpleValidation: enableNumericPhoneValidation(),
      country: region,
    }).then(({ valid, error }) => {
      if (!valid) {
        setPhoneNumberError(getPhoneNumberErrorMessage(error));
      }
    });
  });

  return (
    <DetailsBox accessibilityLabel={sectionHeading}>
      <View style={styles.deliveringToContainer}>
        <Icon accessibilityHidden title="Store" variant="delivery" size="sm" marginRight="$1" />
        <TextStyled bold fontSize="md">
          {`${t('deliveringTo')}:`}
        </TextStyled>
      </View>
      <A11yWrapper>
        <DatadogSessionReplayMask>
          <TextStyled testID="delivery-address" data-private>
            {localizedDeliveryAddress}
          </TextStyled>
        </DatadogSessionReplayMask>
      </A11yWrapper>
      <View style={styles.deliveryEtaContainer}>
        {maxDeliveryTime && (
          <HStack focusable testID="delivery-eta">
            <BaseText accessibilityLabel={t('etaFull')}>{`${t('eta')}: `}</BaseText>
            <BaseText fontWeight="bold">{ETA}</BaseText>
          </HStack>
        )}
      </View>
      <NYCRegulatoryLink />

      <FieldContainer>
        <TextInput
          required={enableRequireAddress2}
          label={enableRequireAddress2 ? t('unitNumberLabelRequired') : t('unitNumberLabel')}
          testID="delivery-address-line2"
          value={deliveryAddress?.addressLine2 || ''}
          onChangeText={handleUnitNumberChange}
          InputRightElement={
            !addressLine2Error && Number(deliveryAddress?.addressLine2?.length) > 0 ? (
              <Box paddingX="$2_5">
                <Icon variant="check" color={getDesignToken('color-validated')} />
              </Box>
            ) : undefined
          }
        />
      </FieldContainer>
      <FieldContainer>
        <FormControl.Label accessible>{t('deliveryDetailsInstructions')}</FormControl.Label>
        <InstructionsTextarea
          onChangeText={text => setDeliveryInstructions(text)}
          placeholder={deliveryInstructions}
          value={deliveryInstructions}
          returnKeyType="done"
          testID="deliveryInstructions"
          maxLength={512}
          date-private
          numberOfLines={1}
        />
      </FieldContainer>
      <FieldContainer>
        <TextInput
          onChangeText={handleOrderPhoneNumberChange}
          testID="delivery-phone-number"
          label={t('phoneNumberRequired')}
          labelProps={{
            _text: {
              color: hasPhoneNumberErrors ? 'error.600' : undefined,
            },
          }}
          value={editingPhoneNumber}
          keyboardType="phone-pad"
          errorMessage={deliveryDetailsErrors.phoneNumber || phoneNumberError}
          _focus={{
            borderColor: hasPhoneNumberErrors ? getDesignToken('border-color-error') : undefined,
          }}
          required
          placeholder={t('phoneNumberPlaceholder')}
          placeholderTextColor={getDesignToken('icon-form-input-default')}
          ref={phoneNumberInputRef}
          InputRightElement={
            !phoneNumberError && orderPhoneNumber ? (
              <Box paddingX="$2_5">
                <Icon variant="check" color={getDesignToken('color-validated')} />
              </Box>
            ) : (
              <Box /> // We need this empty <Box /> to prevent a bigger rerender, avoiding the keyboard for being dismissed during the process
            )
          }
        />
      </FieldContainer>
      <PhoneAddressInformationToggle />
      <SaveAddressSelection />
      {enableFreeDeliveryCheckoutProgress && (
        <FreeDeliveryProgressContainer serverOrder={serverOrder} isLoading={isOrderPriceLoading} />
      )}
    </DetailsBox>
  );
};

const styles = StyleSheet.create({
  deliveringToContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.$1,
  },
  deliveryEtaContainer: {
    marginBottom: spacing.$2,
  },
});

export default DeliveryDetailsForm;
